import { useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowRight, Check, Plus } from "lucide-react";
import type { Answers, CatalogOffering, CatalogTool } from "../../services/catalogApi";
import { type Details, NOT_FOR_ME, type QueueEntry, joinWords, monogram, reasonRows, toolMarks } from "./assessment";
import { BuiltBadge, Chip, FitMeter, InPlanBadge, blueprintPaper, prefersReducedMotion, textButton } from "./ui";

// Where the "today" cards lie scattered before they glide into a tidy stack.
const SETTLE = "cubic-bezier(.2,.8,.2,1)";
const GLIDE = `left .75s ${SETTLE}, top .75s ${SETTLE}, width .75s ${SETTLE}, transform .75s ${SETTLE}, border-color .4s`;
const SCATTER = [{ left: "5%", top: 64, rotate: -6 }, { left: "51%", top: 52, rotate: 5 },
                 { left: "9%", top: 190, rotate: 4 }, { left: "53%", top: 206, rotate: -5 }];

/**
 * One application, laid out like a store page: what it does, why it fits, and what to do next. It shows an item from the
 * visitor's curated list, or one they opened while browsing (`onSkip` left out, and `nextLabel` names where Next goes).
 */
export default function QueueItem({ entry, position, total, added, explanation, answers, details, tools, offerings,
                                    onToggleAdd, onSkip, onNext, nextLabel }: {
  entry: QueueEntry;
  position: number;
  total: number;
  added: boolean;
  explanation: string | null;
  answers: Answers;
  details: Details;
  /** The catalog's tools with their brand marks, from the schema the page already fetched. */
  tools?: (CatalogTool | string)[];
  /** The catalog's services (offerings), to name what is set up alongside this application. */
  offerings?: CatalogOffering[];
  onToggleAdd: () => void;
  onSkip?: (reason: string) => void;
  onNext: () => void;
  nextLabel?: string;
}) {
  const { listing } = entry;
  const [after, setAfter] = useState(false);
  const [notOpen, setNotOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [brokenLogos, setBrokenLogos] = useState<Set<string>>(new Set());
  const marks = toolMarks(tools ?? []);
  const timer = useRef<number>();

  // Show the mess first, then let it settle; choosing a tab stops the automatic switch.
  useEffect(() => {
    setNotOpen(false);
    setReason("");
    if (prefersReducedMotion()) { setAfter(true); return; }
    setAfter(false);
    timer.current = window.setTimeout(() => setAfter(true), 1500);
    return () => window.clearTimeout(timer.current);
  }, [listing.id]);

  const show = (view: boolean) => {
    window.clearTimeout(timer.current);
    setAfter(view);
  };

  const named = listing.works_with.filter((t) => answers.tools.includes(t));
  const others = listing.works_with.filter((t) => !named.includes(t)).slice(0, Math.max(0, 6 - named.length));
  // The same cards in both views: the problems as they pile up today, each one handled afterwards.
  const before = listing.problems.slice(0, 4);
  const cards = [...new Set([...before, ...listing.highlights.map((h) => h.title)])].slice(0, 4);
  if (!cards.length) cards.push(listing.name);
  const remaining = total - position - 1;
  const setUp = listing.set_up_for.length ? joinWords(listing.set_up_for)
    : named.length ? joinWords(named) : "We'll confirm which tools to connect on our call.";
  // Services sold alongside this application, named by the plain card wording the schema carries.
  const needs = (listing.needs ?? []).map((id) => (offerings ?? []).find((o) => o.id === id)?.headline)
    .filter((h): h is string => !!h);
  const worksWith = (
    <span key="works" className="flex flex-wrap gap-1.5">
      {[...named, ...others].map((tool) => {
        const mark = marks.get(tool);
        return (
          <span key={tool} className={`inline-flex items-center gap-1.5 rounded-full border py-1 pl-1 pr-2.5 text-xs font-bold ${
            named.includes(tool) ? "border-primary bg-primary/10" : "border-borderSubtle bg-white"}`}>
            <span aria-hidden="true" className={`flex h-5 w-5 items-center justify-center rounded-md text-[10px] font-extrabold ${
              named.includes(tool) ? "bg-primary text-white" : "bg-slate-100 text-text-secondary"}`}>
              {mark && !brokenLogos.has(tool)
                ? <img src={mark} alt="" loading="lazy" className="h-3.5 w-3.5 object-contain"
                       onError={() => setBrokenLogos((s) => new Set(s).add(tool))} />
                : monogram(tool)}
            </span>
            {tool}
          </span>
        );
      })}
      {!listing.works_with.length && <span className="font-semibold">The tools you already use</span>}
    </span>
  );
  const why = entry.picked ? [] : reasonRows(entry, answers, details, explanation);
  const rows: [string, ReactNode][] = [
    ...(entry.picked ? [] : [["Fit", <FitMeter key="fit" fit={entry.fit} wide />] as [string, ReactNode]]),
    ["Time saved", listing.time_saved],
    ["Runs", listing.where_it_runs],
    ["Works with", worksWith],
    ["Set up for you", setUp],
    ...(needs.length ? [["With it, we also set up", joinWords(needs)] as [string, ReactNode]] : []),
  ];

  return (
    <article aria-labelledby="item-title" className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 id="item-title" className="font-display text-3xl font-bold text-text-primary sm:text-4xl">{listing.name}</h2>
        {listing.temporary && <BuiltBadge />}
        {added && <InPlanBadge />}
      </div>

      <div className="flex flex-wrap items-start gap-8">
        <div className="flex min-w-0 flex-[3_1_520px] flex-col gap-3">
          <div role="img" aria-label={`Today: ${joinWords(before) || "work done by hand"}. After: ${listing.headline}`}
               style={blueprintPaper} className="relative h-[340px] overflow-hidden rounded-2xl border border-primary/20">
            <span className={`absolute left-5 top-4 rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-wider transition-colors duration-500 ${
              after ? "bg-primary text-white" : "bg-amber-100 text-amber-800"}`}>
              {after ? (listing.temporary ? "Built for you" : "After") : (listing.temporary ? "Your answers" : "Today")}
            </span>
            {cards.map((label, i) => {
              const spot = SCATTER[i % SCATTER.length];
              return (
                <div key={label}
                     style={{ transition: GLIDE, ...(after
                       ? { left: 24, top: 64 + i * 62, width: "calc(100% - 48px)", transform: "rotate(0deg)" }
                       : { left: spot.left, top: spot.top, width: "44%", transform: `rotate(${spot.rotate}deg)` }) }}
                     className={`absolute flex items-center gap-2.5 overflow-hidden whitespace-nowrap rounded-xl border-[1.5px] bg-white px-3.5 py-3 text-[15px] font-semibold text-text-primary shadow-md ${
                       after ? "border-primary/40" : "border-amber-200"}`}>
                  <span className={`flex h-5 w-5 flex-none items-center justify-center rounded-full text-white transition-colors duration-[400ms] ${after ? "bg-primary" : "bg-amber-300"}`}>
                    <Check className="h-3 w-3" aria-hidden="true" />
                  </span>
                  <span className="overflow-hidden text-ellipsis">{label}</span>
                  <span className={`ml-auto text-[13px] font-bold text-primary transition-opacity duration-[400ms] ${after ? "opacity-100" : "opacity-0"}`}>
                    {listing.temporary ? "Included" : "Handled"}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div role="group" aria-label="Before and after" className="flex gap-1 rounded-xl bg-slate-100 p-1">
              <button type="button" aria-pressed={!after} onClick={() => show(false)}
                      className={`min-h-[40px] rounded-lg px-4 text-[15px] font-bold text-text-primary ${after ? "" : "bg-white shadow-sm"}`}>
                {listing.temporary ? "Your answers" : "Today"}
              </button>
              <button type="button" aria-pressed={after} onClick={() => show(true)}
                      className={`min-h-[40px] rounded-lg px-4 text-[15px] font-bold text-text-primary ${after ? "bg-white shadow-sm" : ""}`}>
                {listing.temporary ? "Built for you" : "After"}
              </button>
            </div>
            <p className="text-[15px] font-semibold text-text-secondary">{after ? listing.headline : "The work as it happens today."}</p>
          </div>
        </div>

        <div className="flex min-w-0 flex-[2_1_340px] flex-col gap-3.5">
          <p className="font-display text-2xl font-bold leading-snug text-text-primary">{listing.headline}</p>
          <p className="text-text-secondary">{listing.summary}</p>
          <dl className="catalog-stagger flex flex-col border-t border-borderSubtle">
            {rows.map(([term, value]) => (
              <div key={term as string} className="grid grid-cols-[118px_minmax(0,1fr)] items-center gap-3 border-b border-borderSubtle py-3">
                <dt className="text-sm font-bold text-text-muted">{term}</dt>
                <dd className="font-semibold text-text-primary">{value}</dd>
              </div>
            ))}
          </dl>
          {named.length > 0 && <p className="text-sm text-text-muted">Outlined tools are the ones you told us you use.</p>}
        </div>
      </div>

      {why.length > 0 && (
        <div role="region" aria-labelledby="why-title" className="flex flex-col gap-4 rounded-2xl border border-primary/20 bg-primary/5 p-6 sm:p-8">
          <div>
            <h3 id="why-title" className="font-display text-2xl font-bold text-text-primary">Why it's in your curated list</h3>
            <p className="text-text-secondary">Each reason comes from something you told us.</p>
          </div>
          <ol className="catalog-stagger flex flex-col gap-2.5">
            {why.map((row) => (
              <li key={row.text} className="flex flex-col gap-0.5 rounded-xl border border-primary/15 bg-white px-5 py-4">
                <span className="text-xs font-extrabold uppercase tracking-wider text-text-muted">{row.source}</span>
                <span className="font-semibold text-text-primary">{row.text}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {listing.highlights.length > 0 && (
        <div role="region" aria-labelledby="does-title" className="flex flex-col gap-3">
          <h3 id="does-title" className="font-display text-xl font-bold text-text-primary">What it does</h3>
          <div className="catalog-stagger grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {listing.highlights.map((h) => (
              <div key={h.title} className="flex flex-col gap-1 rounded-xl border border-borderSubtle bg-surface p-5">
                <span className="text-lg font-bold text-text-primary">{h.title}</span>
                <span className="text-text-secondary">{h.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="sticky bottom-0 z-20 flex flex-col gap-2.5 pb-4 pt-3.5">
        {notOpen && onSkip && (
          <div className="catalog-enter flex flex-col gap-3 rounded-2xl border border-borderSubtle bg-white p-4 shadow-xl">
            <span id="not-label" className="font-bold text-text-primary">What made it not a fit? (optional)</span>
            <div role="group" aria-labelledby="not-label" className="flex flex-wrap gap-2">
              {NOT_FOR_ME.map((r) => <Chip key={r} pressed={reason === r} onClick={() => setReason(reason === r ? "" : r)}>{r}</Chip>)}
            </div>
            <div className="flex flex-wrap gap-2.5">
              <button type="button" onClick={() => onSkip(reason)} className="min-h-[48px] rounded-full bg-text-primary px-5 font-bold text-white">Skip it</button>
              <button type="button" onClick={() => setNotOpen(false)} className={textButton}>Keep it in my list</button>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-borderSubtle bg-white p-2.5 shadow-xl">
          <button type="button" aria-pressed={added} onClick={onToggleAdd}
                  className={`inline-flex min-h-[52px] flex-auto items-center justify-center gap-2 rounded-xl border-2 px-5 font-bold text-primary ${
                    added ? "border-primary bg-primary/10" : "border-primary/40 bg-white"}`}>
            {added ? <Check className="h-4 w-4" aria-hidden="true" /> : <Plus className="h-4 w-4" aria-hidden="true" />}
            {added ? "In my plan" : "Add to my plan"}
          </button>
          {onSkip && (
            <button type="button" aria-expanded={notOpen} onClick={() => setNotOpen(!notOpen)}
                    className="min-h-[52px] flex-auto rounded-xl border border-borderSubtle bg-white px-4 font-bold text-text-primary">Not for me</button>
          )}
          <button type="button" onClick={onNext}
                  className="-order-1 inline-flex min-h-[60px] flex-[1_1_100%] items-center justify-center gap-2.5 rounded-xl bg-primary px-6 text-lg font-extrabold text-white hover:bg-primaryHover sm:order-none sm:flex-[0_1_auto]">
            {nextLabel ?? (remaining > 0 ? `Next in your list (${remaining} remaining)` : "See my plan")}
            <ArrowRight className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </article>
  );
}
